<script setup>
/**
 * A member's profile in a pop-up, for a restaurant's team — opened from the
 * member's name and photo in the restaurant's Messages (Kapil, 2026-09-25:
 * "clicking on Sid's name along with Sid's profile picture should show his
 * profile in a popup").
 *
 * The same profile the Fans page's "View full profile" shows, loaded the same
 * way, with its rule: the phone number and email stay hidden until the member
 * has booked with the restaurant through RM. With their Favorites and
 * Connections counts, and their Favorites list when their profile page would
 * show it — public, or the viewer is a connection (Kapil, 2026-09-25: "as
 * they would if they were viewing his profile page").
 */
import { ref, watch } from 'vue'
import axios from '@axios'
import { useUsersStore } from '@/stores'
import MemberProfileCard from '@core/components/MemberProfileCard.vue'
import MemberFavorites from '@core/components/MemberFavorites.vue'
import { favoritesVisible } from '@/utils/memberProfileView'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  memberId: { type: String, default: '' },
})

const emit = defineEmits(['update:modelValue'])

const usersStore = useUsersStore()
const member = ref(null)
const loading = ref(false)
const error = ref('')

async function load() {
  if (!props.memberId) return
  loading.value = true
  error.value = ''
  member.value = null
  try {
    const [, detail] = await Promise.all([
      usersStore.fetchUserById(props.memberId),
      axios.get(`/restaurant-admin/fans/${props.memberId}`).catch(() => null),
    ])

    // Re-read from the store: fetchUserById's return value is not the user.
    const base = usersStore.users.find(u => String(u._id) === String(props.memberId)) || null
    const booked = Array.isArray(detail?.data?.bookingHistory) && detail.data.bookingHistory.length > 0

    member.value = base && !booked ? { ...base, phoneNumber: null, email: null } : base
    if (!base) error.value = 'Could not load this profile.'
  } catch {
    error.value = 'Could not load this profile.'
  } finally {
    loading.value = false
  }
}

watch(() => [props.modelValue, props.memberId], ([open]) => {
  if (open) load()
}, { immediate: true })
</script>

<template>
  <VDialog
    :model-value="modelValue"
    max-width="460"
    scrollable
    @update:model-value="emit('update:modelValue', $event)"
  >
    <VCard class="fan-profile-dialog">
      <VBtn
        icon="ri-close-line"
        variant="text"
        size="small"
        class="fan-profile-dialog__close"
        aria-label="Close"
        @click="emit('update:modelValue', false)"
      />
      <div
        v-if="loading && !member"
        class="fan-profile-dialog__loading"
      >
        <VProgressCircular
          indeterminate
          color="primary-dark"
          size="28"
        />
      </div>
      <p
        v-else-if="error"
        class="fan-profile-dialog__error"
      >
        {{ error }}
      </p>
      <template v-else-if="member">
        <MemberProfileCard
          :member="member"
          show-stats
        />
        <MemberFavorites
          v-if="favoritesVisible(member)"
          :member="member"
          class="fan-profile-dialog__favorites"
        />
      </template>
    </VCard>
  </VDialog>
</template>

<style scoped>
.fan-profile-dialog {
  position: relative;
  padding: 16px;
  overflow-y: auto;
}

.fan-profile-dialog__favorites {
  margin-top: 16px;
}

.fan-profile-dialog__close {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
}

.fan-profile-dialog__loading {
  display: flex;
  justify-content: center;
  padding: 32px 0;
}

.fan-profile-dialog__error {
  margin: 24px 0;
  font-size: 14px;
  color: rgb(var(--v-theme-error));
}
</style>
